/**
 * TUTOR panel (M3): grading queue for the cohorts I review, with
 * ordering by age/SLA and an overdue indicator, and a submission review
 * panel (content + rubric + history → feedback + result).
 *
 * Local-first: pending `submission_version` travels via my_cohorts_teacher /
 * org_admin_scope; SLA config via academy_org. The review's write goes
 * through sync (M3 write-path, which applies the real reviewer authz).
 */
import { type Component, createMemo, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { AlertTriangle, ClipboardCheck } from "@fleet/icons";
import { isOverdue, type ReviewOutcome, reviewVersionMutation, slaDueAt } from "@space/shared";
import type { SubmissionVersion } from "@space/powersync";
import { useI18n } from "@/providers/i18n-provider";
import { useSession } from "@/lib/auth";
import { useDrizzle } from "@/hooks/use-drizzle";
import { Button } from "@/components/ui/button";
import { showToast } from "@/components/ui/toast";
import { Skeleton } from "@fleet/ui";
import { useActiveOrg } from "../hooks/use-active-org";
import { SubmissionHistory } from "./submission-thread";
import { useAllAssignments, useReviewQueue, useRosterUsers, useTutoringSettings } from "./hooks";

function fmt(iso: string | null | undefined): string {
  if (!iso) {
    return "";
  }
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

type SortMode = "sla" | "newest";

export const TutorPanel: Component = () => {
  const { t } = useI18n();
  const { userId } = useSession();
  const { activeOrgId } = useActiveOrg();
  const drizzle = useDrizzle();

  const queue = useReviewQueue();
  const assignments = useAllAssignments();
  const users = useRosterUsers();
  const settings = useTutoringSettings(activeOrgId);

  const [sort, setSort] = createSignal<SortMode>("sla");
  const [selected, setSelected] = createSignal<SubmissionVersion | null>(null);
  const [feedback, setFeedback] = createSignal("");
  const [busy, setBusy] = createSignal(false);

  const slaDays = createMemo(() => (settings.data ?? [])[0]?.sla_days ?? 3);

  const titleOf = (id: string | null | undefined) =>
    (assignments.data ?? []).find((a) => a.id === id)?.title ?? t(msg`Assignment`);
  const nameOf = (id: string | null | undefined) => {
    const u = (users.data ?? []).find((x) => x.id === id);
    return u?.name ?? u?.email ?? id ?? "";
  };
  const overdue = (v: SubmissionVersion) => isOverdue(v.submitted_at ?? "", slaDays());

  const sorted = createMemo(() => {
    const rows = [...(queue.data ?? [])];
    if (sort() === "newest") {
      return rows.toSorted((a, b) => (b.submitted_at ?? "").localeCompare(a.submitted_at ?? ""));
    }
    // We capture the reactive SLA once inside the memo (tracked scope) so
    // the comparator is a pure function, with no reactive reads of its own.
    // oxlint-disable-next-line solid/reactivity -- intended snapshot: the comparator runs synchronously inside this memo, so the captured SLA cannot go stale.
    const days = slaDays();
    const isOv = (v: SubmissionVersion) => isOverdue(v.submitted_at ?? "", days);
    // SLA: overdue ones first, then by age (older = more urgent).
    return rows.toSorted((a, b) => {
      const ov = Number(isOv(b)) - Number(isOv(a));
      return ov === 0 ? (a.submitted_at ?? "").localeCompare(b.submitted_at ?? "") : ov;
    });
  });

  const review = async (outcome: ReviewOutcome) => {
    const db = drizzle();
    const v = selected();
    if (!db || !v) {
      return;
    }
    setBusy(true);
    try {
      await reviewVersionMutation(db, {
        versionId: v.id,
        reviewerUserId: userId() ?? "",
        outcome,
        feedback: feedback().trim() || null,
      });
      showToast(
        outcome === "returned"
          ? t(msg`Returned for changes`)
          : outcome === "approved"
            ? t(msg`Approved`)
            : t(msg`Rejected`),
        "success",
      );
      setSelected(null);
      setFeedback("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div class="grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
      {/* Cola */}
      <section class="space-y-3">
        <div class="flex items-center justify-between gap-2">
          <h2 class="flex items-center gap-2 font-display text-lg font-bold">
            <ClipboardCheck class="size-5" />
            {t(msg`Correction queue`)}
          </h2>
          <select
            class="rounded-lg border-2 border-border bg-field-background px-2 py-1 text-sm"
            value={sort()}
            onChange={(e) => setSort(e.currentTarget.value === "newest" ? "newest" : "sla")}
            data-testid="tutor-queue-sort"
            aria-label={t(msg`Sort`)}
          >
            <option value="sla">{t(msg`By due date (SLA)`)}</option>
            <option value="newest">{t(msg`Newest first`)}</option>
          </select>
        </div>
        <Show when={!queue.isLoading} fallback={<Skeleton class="h-40 w-full rounded-2xl" />}>
          <Show
            when={sorted().length > 0}
            fallback={
              <p class="text-muted-foreground" data-testid="tutor-queue-empty">
                {t(msg`Nothing to review right now. Great job!`)}
              </p>
            }
          >
            <ul class="space-y-2" data-testid="tutor-queue">
              <For each={sorted()}>
                {(v) => (
                  <li>
                    <button
                      type="button"
                      class="w-full rounded-xl border-2 p-3 text-left transition-colors hover:border-primary"
                      classList={{
                        "border-primary bg-primary/5": selected()?.id === v.id,
                        "border-border": selected()?.id !== v.id,
                      }}
                      onClick={() => {
                        setSelected(v);
                        setFeedback("");
                      }}
                      data-testid="tutor-queue-item"
                    >
                      <div class="flex items-center justify-between gap-2">
                        <span class="font-semibold">{titleOf(v.assignment_id)}</span>
                        <Show when={overdue(v)}>
                          <span
                            class="inline-flex items-center gap-1 rounded-full bg-destructive/15 px-2 py-0.5 text-xs font-medium text-destructive"
                            data-testid="tutor-queue-overdue"
                          >
                            <AlertTriangle class="size-3" />
                            {t(msg`Overdue`)}
                          </span>
                        </Show>
                      </div>
                      <p class="mt-0.5 text-sm text-muted-foreground">
                        {nameOf(v.user_id)} ·{" "}
                        {(() => {
                          const version = v.version ?? 0;
                          return t(msg`v${version}`);
                        })()}{" "}
                        · {fmt(v.submitted_at)}
                      </p>
                      <p class="text-xs text-muted-foreground">
                        {(() => {
                          const due = fmt(slaDueAt(v.submitted_at ?? "", slaDays()).toISOString());
                          return t(msg`Due ${due}`);
                        })()}
                      </p>
                    </button>
                  </li>
                )}
              </For>
            </ul>
          </Show>
        </Show>
      </section>

      {/* Review */}
      <section class="space-y-3">
        <Show
          when={selected()}
          fallback={
            <p class="rounded-2xl border-2 border-dashed border-border p-6 text-center text-muted-foreground">
              {t(msg`Select a submission from the queue to review it.`)}
            </p>
          }
        >
          {(v) => (
            <div
              class="space-y-3 rounded-2xl border-2 border-border p-4"
              data-testid="tutor-review-panel"
            >
              <h3 class="font-display text-lg font-bold">{titleOf(v().assignment_id)}</h3>
              <p class="text-sm text-muted-foreground">{nameOf(v().user_id)}</p>
              <SubmissionHistory
                assignmentId={v().assignment_id ?? ""}
                cohortId={v().cohort_id ?? ""}
                userId={v().user_id ?? ""}
              />
              <div class="space-y-2">
                <label class="text-sm font-semibold" for="tutor-feedback">
                  {t(msg`Feedback`)}
                </label>
                <textarea
                  id="tutor-feedback"
                  class="min-h-28 w-full rounded-lg border-2 border-border bg-field-background p-2 text-sm focus-visible:border-primary focus-visible:outline-none"
                  placeholder={t(msg`What should the student improve?`)}
                  value={feedback()}
                  onInput={(e) => setFeedback(e.currentTarget.value)}
                  data-testid="tutor-feedback"
                />
                <div class="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    onClick={() => review("returned")}
                    disabled={busy()}
                    data-testid="tutor-return"
                  >
                    {t(msg`Return for changes`)}
                  </Button>
                  <Button
                    onClick={() => review("approved")}
                    disabled={busy()}
                    data-testid="tutor-approve"
                  >
                    {t(msg`Approve`)}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => review("rejected")}
                    disabled={busy()}
                    data-testid="tutor-reject"
                  >
                    {t(msg`Reject`)}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Show>
      </section>
    </div>
  );
};
